if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoomScale = 2;
        this.panX = 0;
        this.panY = 0;
        this.didDrag = false;
        this.dragState = null;

        this.addEventListener('click', this.onImageClick.bind(this));
        this.addEventListener('pointerdown', this.onImagePointerDown.bind(this));
        this.addEventListener('pointermove', this.onImagePointerMove.bind(this));
        this.addEventListener('pointerup', this.onImagePointerUp.bind(this));
        this.addEventListener('pointercancel', this.onImagePointerUp.bind(this));
        this.querySelector('.product-media-modal__thumbnails')?.addEventListener(
          'click',
          this.onThumbnailClick.bind(this)
        );
      }

      hide() {
        const activeMediaId =
          this.querySelector('.product-media-modal__content > .active')?.dataset.mediaId ||
          this.openedBy?.getAttribute('data-media-id');
        this.resetZoom();
        super.hide();

        if (!activeMediaId || !this.dataset.section) return;
        window.requestAnimationFrame(() => {
          const gallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
          gallery?.setActiveMedia(`${this.dataset.section}-${activeMediaId}`, false);
        });
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.showMedia(this.openedBy.getAttribute('data-media-id'));
      }

      showMedia(mediaId) {
        this.resetZoom();
        const mediaContainer = this.querySelector('.product-media-modal__content');
        mediaContainer.querySelectorAll(':scope > [data-media-id]').forEach((element) => {
          element.classList.toggle('active', element.dataset.mediaId === mediaId);
        });
        const activeMedia = mediaContainer.querySelector(`:scope > [data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        if (!this.classList.contains('product-media-modal--gallery')) {
          activeMedia.scrollIntoView();
        }

        const container = this.querySelector('[role="document"]');
        container.scrollTop = 0;
        container.scrollLeft = Math.max(0, (activeMedia.width - container.clientWidth) / 2);
        this.setActiveModalThumbnail(mediaId);

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      onThumbnailClick(event) {
        const thumbnail = event.target.closest('[data-modal-thumbnail]');
        if (!thumbnail) return;

        event.preventDefault();
        this.showMedia(thumbnail.dataset.modalThumbnail);
      }

      setActiveModalThumbnail(mediaId) {
        this.querySelectorAll('[data-modal-thumbnail]').forEach((thumbnail) => {
          const isActive = thumbnail.dataset.modalThumbnail === mediaId;
          thumbnail.classList.toggle('is-active', isActive);
          if (isActive) {
            thumbnail.setAttribute('aria-current', 'true');
            thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          } else {
            thumbnail.removeAttribute('aria-current');
          }
        });
      }

      getModalImage(event) {
        return event.target.closest?.('.product-media-modal__content > img');
      }

      onImageClick(event) {
        const image = this.getModalImage(event);
        if (!image) return;

        event.preventDefault();
        if (this.didDrag) {
          this.didDrag = false;
          return;
        }

        if (image.classList.contains('is-zoomed')) {
          this.resetZoom(image);
        } else {
          this.zoomImage(image);
        }
      }

      zoomImage(image) {
        this.resetZoom();
        this.panX = 0;
        this.panY = 0;
        image.style.setProperty('--zoom-x', '0px');
        image.style.setProperty('--zoom-y', '0px');
        image.classList.add('is-zoomed');
        this.classList.add('is-image-zoomed');
      }

      resetZoom(image = null) {
        const images = image
          ? [image]
          : Array.from(this.querySelectorAll('.product-media-modal__content > img'));

        images.forEach((modalImage) => {
          modalImage.classList.remove('is-zoomed', 'is-dragging');
          modalImage.style.removeProperty('--zoom-x');
          modalImage.style.removeProperty('--zoom-y');
        });

        this.dragState = null;
        this.panX = 0;
        this.panY = 0;
        if (!this.querySelector('.product-media-modal__content > img.is-zoomed')) {
          this.classList.remove('is-image-zoomed');
        }
      }

      onImagePointerDown(event) {
        const image = this.getModalImage(event);
        if (!image?.classList.contains('is-zoomed')) return;

        event.preventDefault();
        this.didDrag = false;
        this.dragState = {
          image,
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          originX: this.panX,
          originY: this.panY,
        };
        image.classList.add('is-dragging');
        image.setPointerCapture?.(event.pointerId);
      }

      onImagePointerMove(event) {
        if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;

        event.preventDefault();
        const deltaX = event.clientX - this.dragState.startX;
        const deltaY = event.clientY - this.dragState.startY;
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) this.didDrag = true;

        const maxX = (this.dragState.image.offsetWidth * (this.zoomScale - 1)) / 2;
        const maxY = (this.dragState.image.offsetHeight * (this.zoomScale - 1)) / 2;
        this.panX = Math.max(-maxX, Math.min(maxX, this.dragState.originX + deltaX));
        this.panY = Math.max(-maxY, Math.min(maxY, this.dragState.originY + deltaY));

        this.dragState.image.style.setProperty('--zoom-x', `${this.panX}px`);
        this.dragState.image.style.setProperty('--zoom-y', `${this.panY}px`);
      }

      onImagePointerUp(event) {
        if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;

        this.dragState.image.classList.remove('is-dragging');
        if (this.dragState.image.hasPointerCapture?.(event.pointerId)) {
          this.dragState.image.releasePointerCapture(event.pointerId);
        }
        this.dragState = null;
      }
    }
  );
}
